// ─────────────────────────────────────────────────────────────
// src/hooks/useBulkCalculator.js
// All state and logic for the bulk-Excel processing feature.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import * as XLSX from "xlsx";
import { calculateNetFromGross, calculateGrossFromNet } from "../utils/taxCalculations";

/**
 * Safely reads a field from an Excel row, trying multiple key variants.
 */
const readField = (row, keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== "") return row[key];
  }
  return undefined;
};

/**
 * @param {'netFromGross'|'grossFromNet'} calculationMode
 * @param {string} language  – 'en' | 'ar'
 * @param {object} t         – translation strings
 */
const useBulkCalculator = (calculationMode, language, t) => {
  const [file,          setFile]          = useState(null);
  const [processing,    setProcessing]    = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [bulkError,     setBulkError]     = useState(null);
  const [success,       setSuccess]       = useState(false);

  const handleFileUpload = (e) => {
    const uploaded = e.target.files[0];
    if (uploaded) {
      setFile(uploaded);
      setBulkError(null);
      setSuccess(false);
      setProcessedData(null);
    }
  };

  const processExcel = async () => {
    if (!file) {
      setBulkError(t.pleaseUploadFile);
      return;
    }

    setProcessing(true);
    setBulkError(null);

    try {
      const buffer   = await file.arrayBuffer();
      const workbook = XLSX.read(buffer);
      const sheet    = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(sheet);

      if (jsonData.length === 0) throw new Error(t.excelFileEmpty);

      const processedRows = jsonData
        .map((row, index) => {
          try {
            const employeeName   = readField(row, ["Employee Name", "employee name", "EmployeeName", "اسم الموظف"]) ?? "";
            const insuranceSalary = parseFloat(readField(row, ["Insurance Salary", "insurance salary", "InsuranceSalary", "راتب التأمينات"]) ?? 0);

            if (calculationMode === "netFromGross") {
              const grossSalary = parseFloat(readField(row, ["Gross Salary", "gross salary", "GrossSalary", "الراتب الإجمالي"]) ?? 0);
              if (!grossSalary || !insuranceSalary) throw new Error(`${t.invalidDataInRow} ${index + 2}`);

              const { netSalary, monthlyTax, yearlyTax, martyrsFund } = calculateNetFromGross(grossSalary, insuranceSalary);

              return {
                [t.employeeName]:   employeeName,
                [t.insuranceSalary]: insuranceSalary.toFixed(2),
                [t.netSalary]:      netSalary.toFixed(2),
                [t.grossSalary]:    grossSalary.toFixed(2),
                [t.monthlyTax]:     monthlyTax.toFixed(2),
                [t.yearlyTax]:      yearlyTax.toFixed(2),
                [t.martyrsFund]:    martyrsFund.toFixed(2),
              };
            } else {
              const netSalary = parseFloat(readField(row, ["Net Salary", "net salary", "NetSalary", "الراتب الصافي"]) ?? 0);
              if (!netSalary || !insuranceSalary) throw new Error(`${t.invalidDataInRow} ${index + 2}`);

              const grossSalary = calculateGrossFromNet(netSalary, insuranceSalary);
              const { netSalary: calcNet, monthlyTax, yearlyTax, martyrsFund } = calculateNetFromGross(grossSalary, insuranceSalary);

              return {
                [t.employeeName]:   employeeName,
                [t.insuranceSalary]: insuranceSalary.toFixed(2),
                [t.netSalary]:      calcNet.toFixed(2),
                [t.grossSalary]:    grossSalary.toFixed(2),
                [t.monthlyTax]:     monthlyTax.toFixed(2),
                [t.yearlyTax]:      yearlyTax.toFixed(2),
                [t.martyrsFund]:    martyrsFund.toFixed(2),
              };
            }
          } catch (err) {
            console.error(`Error processing row ${index + 2}:`, err);
            return null;
          }
        })
        .filter(Boolean);

      if (processedRows.length === 0) throw new Error(t.noValidData);

      setProcessedData(processedRows);
      setSuccess(true);
    } catch (err) {
      setBulkError(err.message || t.errorProcessingFile);
    } finally {
      setProcessing(false);
    }
  };

  const downloadExcel = () => {
    if (!processedData) return;

    const worksheet  = XLSX.utils.json_to_sheet(processedData);
    const workbook   = XLSX.utils.book_new();
    const sheetLabel = language === "ar" ? "الرواتب المعالجة" : "Processed Salaries";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetLabel);

    const fileName = `Processed_Salaries_${calculationMode}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const resetBulk = () => {
    setFile(null);
    setProcessedData(null);
    setBulkError(null);
    setSuccess(false);
  };

  return {
    file,
    processing,
    processedData,
    bulkError,
    success,
    handleFileUpload,
    processExcel,
    downloadExcel,
    resetBulk,
  };
};

export default useBulkCalculator;